import { CSSProperties, useMemo } from 'react'
import { Icon, SemanticICONS } from 'semantic-ui-react'

export type DataStatus =
  | 'data-flowing'
  | 'no-observation'
  | 'technical-day'
  | 'rfi-measurements'
  | 'correction-measurements'
  | 'no-data-received'
  | 'unknown'

interface DataStatusIndicatorProps {
  label: string
  status: DataStatus
  /** Overrides the status text, e.g. an unknown status reported by the telescope. */
  text?: string
  /** Tooltip shown on hover. */
  title?: string
}

interface StatusConfig {
  color: string
  bgColor: string
  text: string
  icon?: SemanticICONS
}

const STATUS_CONFIG: Record<DataStatus, StatusConfig> = {
  'data-flowing': {
    color: '#ffffff',
    bgColor: '#21ba45',
    text: 'Data flowing',
    icon: 'check',
  },
  'no-observation': {
    color: '#ffffff',
    bgColor: '#767676',
    text: 'No observation',
    icon: undefined,
  },
  'technical-day': {
    color: '#000000',
    bgColor: '#fbbd08',
    text: 'Technical day',
    icon: 'wrench',
  },
  'rfi-measurements': {
    color: '#ffffff',
    bgColor: '#2185d0',
    text: 'RFI measurements',
    icon: 'signal',
  },
  'correction-measurements': {
    color: '#ffffff',
    bgColor: '#f2711c',
    text: 'Correction measurements',
    icon: 'sync',
  },
  'no-data-received': {
    color: '#ffffff',
    bgColor: '#db2828',
    text: 'Sun dataflow, no data received',
    icon: 'warning sign',
  },
  unknown: {
    color: '#ffffff',
    bgColor: '#767676',
    text: 'Unknown status',
    icon: undefined,
  },
}

const badgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
}

function DataStatusIndicator({ label, status, text, title }: DataStatusIndicatorProps) {
  const config = STATUS_CONFIG[status]

  const style: CSSProperties = useMemo(
    () => ({
      ...badgeStyle,
      color: config.color,
      backgroundColor: config.bgColor,
    }),
    [config]
  )

  return (
    <div style={style} title={title} data-status={status}>
      {config.icon && <Icon name={config.icon} size='small' style={{ margin: 0 }} />}
      <span>
        {label}: {text ?? config.text}
      </span>
    </div>
  )
}

export default DataStatusIndicator
